import { Context, Middleware } from '@nuxt/types'
import { RequestException } from '~/types/exceptions'
import { Page } from '~/model/PagesModel'
import { fillOwnProperties } from '~/shared/utils/helpers'
import { isUnknownObject } from '~/shared/utils/guards'
import { UnknownObject } from '~/shared/interfaces'
import { ImageService } from '~/services/Misc/ImageService'
import { BreadcrumbModel } from '~/model/BreadCrumbs'

const normalizePath = (path: string): string => {
  if (!path || path === '/') return 'main'
  const normalized = path.replace(/^\/+|\/+$/g, '')
  return normalized.includes('..') || normalized.includes('//') ? 'main' : normalized || 'main'
}

const getSeoMetadata = async (context: Context): Promise<Page> => {
  if (context.route.name?.startsWith('account')) {
    return new Page()
  }

  const token = context.$services.user.getCookieToken()
  if (token) {
    context.$axios.defaults.headers.common['Authorization-SF-Token'] = token
  }

  try {
    const params: UnknownObject = { url: normalizePath(context.route.path) }
    const list = await context.$services.pagesService.metaDataService.getList(params)

    if (!list.data?.length || !isUnknownObject(list.data[0])) {
      return new Page()
    }

    const data = list.data[0]
    const page = new Page()
    fillOwnProperties(page, data)

    if (typeof data.image === 'string' && data.image) {
      const imageService = new ImageService()
      page.image = context.$config.hosts.externalHost + imageService.cropImage(data.image, {})
    }

    const externalHost = context.$config.hosts.externalHost
    page.url = externalHost + context.route.fullPath

    const canonical =
      typeof (data as any).canonical_link === 'string' ? (data as any).canonical_link : context.route.path

    try {
      const canonicalUrl = new URL(canonical, externalHost)
      canonicalUrl.search = ''
      canonicalUrl.hash = ''
      page.canonical_url = canonicalUrl.toString()
    } catch {
      page.canonical_url = ''
    }

    return page
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching SEO metadata:', error)
    return new Page()
  }
}

const getDefaultBreadcrumbs = (path: string): BreadcrumbModel[] => {
  const breadcrumbs = [new BreadcrumbModel({ title: 'Главная', url: '/' })]

  if (!path || path === '/') return breadcrumbs

  const segments = path.split('/').filter(Boolean)
  if (segments.length) {
    breadcrumbs.push(
      new BreadcrumbModel({
        title: decodeURIComponent(segments[segments.length - 1]) || 'Текущая страница',
        url: path
      })
    )
  }

  return breadcrumbs
}

const getBreadCrumbs = async (context: Context): Promise<BreadcrumbModel[]> => {
  try {
    const path = normalizePath(context.route.path)
    const fullPath = path === 'main' ? 'main' : `main/${path}`

    if (!context.$services?.breadcrumbs?.getListModel) {
      return getDefaultBreadcrumbs(context.route.path)
    }

    const result = await context.$services.breadcrumbs.getListModel(fullPath)
    return Array.isArray(result) ? result : getDefaultBreadcrumbs(context.route.path)
  } catch (error) {
    return getDefaultBreadcrumbs(context.route.path)
  }
}

const pageMetadataMiddleware: Middleware = async (context: Context) => {
  try {
    const [seoMetadata, breadcrumbs] = await Promise.allSettled([getSeoMetadata(context), getBreadCrumbs(context)])

    if (seoMetadata.status === 'fulfilled') {
      await context.$tstore.pageMetadata.setSeoMetadata(seoMetadata.value)
    } else {
      const error = seoMetadata.reason
      if (error instanceof RequestException && error.internalError?.status === 404) {
        context.error({ statusCode: 404, message: 'Page not found' })
        return
      }
      // eslint-disable-next-line no-console
      console.error('SEO metadata error:', error)
    }

    context.$tstore.pageMetadata.setBreadcrumbs(
      breadcrumbs.status === 'fulfilled' ? breadcrumbs.value : getDefaultBreadcrumbs(context.route.path)
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Middleware error:', error)
  }
}

export default pageMetadataMiddleware
